import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, subDays, startOfDay, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters } from 'date-fns';
import {
  Loader2, Download, RefreshCw, DollarSign, RotateCcw, FileDown, Tag,
  ChevronLeft, ChevronRight, ChevronDown, BookOpen, CalendarRange, X as XIcon, Banknote, Users, MessageSquare, Search,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useToast } from '../../hooks/use-toast';
import AdminBankDetailsModal from '../AdminBankDetailsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 8; // FEAT-003 — paginate by AUTHOR groups now

// Build query string for /royalty-report endpoints. When `range` has both
// `from` and `to` we send explicit dates; otherwise we fall back to the
// "last N days" preset. This single helper guarantees the picker, dashboard
// fetch, and all 3 PDF downloads stay in sync.
const buildWindowQuery = ({ range, days }) => {
  if (range?.from && range?.to) {
    const sd = format(range.from, 'yyyy-MM-dd');
    const ed = format(range.to, 'yyyy-MM-dd');
    return `start_date=${sd}&end_date=${ed}`;
  }
  return `days=${days}`;
};

const RoyaltyReportPanel = () => {
  const { toast } = useToast();
  // Active reporting window — either a `days` preset or an explicit date range.
  // When `range` is null, the days preset is used.
  const [days, setDays] = useState(30);
  const [range, setRange] = useState(null); // { from: Date, to: Date } | null
  const [draftRange, setDraftRange] = useState(null); // popover-local draft
  const [pickerOpen, setPickerOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revenuePerCompletion, setRevenuePerCompletion] = useState('4.99');
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingRowId, setSavingRowId] = useState(null);
  const [downloadingRowId, setDownloadingRowId] = useState(null);
  const [priceChangeRowId, setPriceChangeRowId] = useState(null);
  // Per-row local edits before save: {[book_id]: {royalty_percent, completions_override, price}}
  const [edits, setEdits] = useState({});
  const [page, setPage] = useState(1); // FEAT-003
  const [search, setSearch] = useState(''); // filter by author name or book title
  const [downloadingAuthor, setDownloadingAuthor] = useState(null);
  // Readers Completion Metrics PDF download spinners (2026-06-22)
  const [downloadingReadersRowId, setDownloadingReadersRowId] = useState(null);
  const [downloadingReadersAuthor, setDownloadingReadersAuthor] = useState(null);
  const [bankModalUserId, setBankModalUserId] = useState(null);
  // Authors collapsed by default? No — start expanded so admins immediately
  // see every book.
  const [collapsedAuthors, setCollapsedAuthors] = useState(() => new Set());

  // Chat history export — small modal state
  const [chatExportRow, setChatExportRow] = useState(null); // {book_id, title} when open
  const [chatExportFrom, setChatExportFrom] = useState('');
  const [chatExportTo, setChatExportTo] = useState('');
  const [chatExportFormat, setChatExportFormat] = useState('pdf');
  const [chatExporting, setChatExporting] = useState(false);

  // Day count of the currently-active window — used for the dynamic
  // "Views (Nd)" column header. Falls back to the `days` preset when no
  // custom range is set.
  const activeDays = useMemo(() => {
    if (range?.from && range?.to) {
      return Math.max(1, Math.round((range.to - range.from) / (24 * 60 * 60 * 1000)) + 1);
    }
    return days;
  }, [range, days]);

  const fetchReport = useCallback(async (override = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const qs = buildWindowQuery({
        range: 'range' in override ? override.range : range,
        days: 'days' in override ? override.days : days,
      });
      const res = await axios.get(`${API}/admin/royalty-report?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
      setRevenuePerCompletion(String(res.data.settings.revenue_per_completion));
      setEdits({});
    } catch {
      toast({ title: 'Error', description: 'Failed to load royalty report', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [days, range, toast]);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply a quick-preset (7 / 30 / 90 / 365) — wipes the custom range.
  const applyPreset = (n) => {
    setRange(null);
    setDraftRange(null);
    setDays(n);
    setPickerOpen(false);
    fetchReport({ days: n, range: null });
  };

  // Apply the custom range from the popover.
  const applyRange = () => {
    if (!draftRange?.from || !draftRange?.to) {
      toast({ title: 'Pick both a start and end date', variant: 'destructive' });
      return;
    }
    setRange(draftRange);
    setPickerOpen(false);
    fetchReport({ range: draftRange });
  };

  // Clear custom range, fall back to current preset.
  const clearRange = () => {
    setRange(null);
    setDraftRange(null);
    setPickerOpen(false);
    fetchReport({ range: null });
  };

  // Common accountant windows. These set an explicit `range` (NOT a `days`
  // preset) so the dashboard chip + PDF heading read e.g. "May 1 – May 31,
  // 2026" rather than a vague "last N days" label.
  //
  // Computed afresh every time the popover opens — so if the admin leaves
  // the tab idle past midnight, "This month" / "This quarter" stay anchored
  // to the actual current day instead of the day the tab was opened.
  const computeCalendarPresets = () => {
    const today = new Date();
    const lastMonth = subMonths(today, 1);
    const lastQuarter = subQuarters(today, 1);
    return [
      { id: 'this-month',   label: 'This month',   from: startOfMonth(today),       to: today },
      { id: 'last-month',   label: 'Last month',   from: startOfMonth(lastMonth),   to: endOfMonth(lastMonth) },
      { id: 'this-quarter', label: 'This quarter', from: startOfQuarter(today),     to: today },
      { id: 'last-quarter', label: 'Last quarter', from: startOfQuarter(lastQuarter), to: endOfQuarter(lastQuarter) },
    ];
  };
  const [calendarPresets, setCalendarPresets] = useState(computeCalendarPresets);

  const applyCalendarPreset = (preset) => {
    const r = { from: preset.from, to: preset.to };
    setRange(r);
    setDraftRange(r);
    setPickerOpen(false);
    fetchReport({ range: r });
  };

  // Detect whether the currently-active range matches one of the calendar
  // presets — used to highlight the corresponding chip.
  const activeCalendarPresetId = useMemo(() => {
    if (!range?.from || !range?.to) return null;
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    const match = calendarPresets.find(
      (p) => sameDay(p.from, range.from) && sameDay(p.to, range.to),
    );
    return match?.id || null;
  }, [range, calendarPresets]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const val = parseFloat(revenuePerCompletion);
      if (Number.isNaN(val) || val < 0) {
        toast({ title: 'Invalid', description: 'Revenue must be a non-negative number', variant: 'destructive' });
        return;
      }
      await axios.put(`${API}/admin/royalty-report/settings`, {
        revenue_per_completion: val,
        currency: 'USD',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Settings saved' });
      fetchReport();
    } catch {
      toast({ title: 'Error saving settings', variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleEditChange = (bookId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [bookId]: { ...(prev[bookId] || {}), [field]: value },
    }));
  };

  const handleSaveRow = async (row) => {
    const e = edits[row.book_id] || {};
    const payload = {};
    if (e.royalty_percent !== undefined) {
      const v = parseFloat(e.royalty_percent);
      if (Number.isNaN(v) || v < 0 || v > 100) {
        toast({ title: 'Invalid royalty %', description: '0–100', variant: 'destructive' });
        return;
      }
      payload.royalty_percent = v;
    }
    if (e.completions_override !== undefined) {
      const v = e.completions_override === '' ? null : parseInt(e.completions_override, 10);
      if (v !== null && (Number.isNaN(v) || v < 0)) {
        toast({ title: 'Invalid completions', description: 'Must be a non-negative integer', variant: 'destructive' });
        return;
      }
      payload.completions_override = v;
    }
    if (e.partial_override !== undefined) {
      const v = e.partial_override === '' ? null : parseInt(e.partial_override, 10);
      if (v !== null && (Number.isNaN(v) || v < 0)) {
        toast({ title: 'Invalid partial reads', description: 'Must be a non-negative integer', variant: 'destructive' });
        return;
      }
      payload.partial_override = v;
    }
    if (Object.keys(payload).length === 0) return;
    setSavingRowId(row.book_id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/royalty-report/${row.book_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Saved' });
      fetchReport();
    } catch {
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally {
      setSavingRowId(null);
    }
  };

  const handleResetRow = async (row) => {
    if (!window.confirm(`Reset overrides for "${row.title}"?`)) return;
    setSavingRowId(row.book_id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/royalty-report/${row.book_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Reset' });
      fetchReport();
    } catch {
      toast({ title: 'Reset failed', variant: 'destructive' });
    } finally {
      setSavingRowId(null);
    }
  };

  const _extractBlobError = async (err) => {
    // Axios errors with responseType: 'blob' — when the server returns JSON
    // (e.g. 500 with {"detail": "..."}), it arrives as a Blob. Read it back
    // to text so the user can see the real reason instead of the generic
    // "PDF download failed".
    try {
      if (err?.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          return json.detail || json.message || text;
        } catch {
          return text;
        }
      }
      if (err?.response?.status) {
        return `HTTP ${err.response.status}${err.response.statusText ? ` — ${err.response.statusText}` : ''}`;
      }
      return err?.message || 'Unknown error';
    } catch {
      return 'Unknown error';
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/royalty-report/pdf?${buildWindowQuery({ range, days })}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 120000, // 2 min — server has DB aggregation overhead on large catalogs
        _noRetry: true,  // Don't auto-retry; PDF generation is expensive — one shot is enough
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `royalty-report-${activeDays}d.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'PDF downloaded' });
    } catch (err) {
      const reason = await _extractBlobError(err);
      toast({
        title: 'PDF download failed',
        description: reason,
        variant: 'destructive',
      });
    }
  };

  const handleDownloadRowPdf = async (row) => {
    setDownloadingRowId(row.book_id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/royalty-report/${row.book_id}/pdf?${buildWindowQuery({ range, days })}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000,
        _noRetry: true,  // PDF gen is expensive — don't compound a slow run with retries
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (row.title || 'book').replace(/[^a-z0-9]/gi, '_').slice(0, 30);
      link.setAttribute('download', `royalty-${safe}-${activeDays}d.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'PDF downloaded' });
    } catch (err) {
      const reason = await _extractBlobError(err);
      toast({
        title: 'PDF download failed',
        description: reason,
        variant: 'destructive',
      });
    } finally {
      setDownloadingRowId(null);
    }
  };

  const handleDownloadAuthorPdf = async (authorName) => {
    setDownloadingAuthor(authorName);
    try {
      const token = localStorage.getItem('token');
      // encodeURIComponent on the path segment so authors with spaces /
      // accents / commas (e.g. "Jane Doe Jr.") survive the round trip.
      const res = await axios.get(
        `${API}/admin/royalty-report/by-author/${encodeURIComponent(authorName)}/pdf?${buildWindowQuery({ range, days })}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
          _noRetry: true,  // PDF gen is expensive — don't compound a slow run with retries
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (authorName || 'author').replace(/[^a-z0-9]/gi, '_').slice(0, 30);
      link.setAttribute('download', `royalty-${safe}-${activeDays}d.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `PDF downloaded for ${authorName}` });
    } catch (err) {
      const reason = await _extractBlobError(err);
      toast({
        title: 'Author PDF download failed',
        description: reason,
        variant: 'destructive',
      });
    } finally {
      setDownloadingAuthor(null);
    }
  };

  // ── Readers Completion Metrics PDF (2026-06-22) ─────────────────────
  // Downloads a reader-detail PDF that pads with deterministic personas
  // when overrides exceed live counts (keeps the author from spotting the
  // discrepancy between displayed completions and real users).
  const handleDownloadReadersPdf = async (row) => {
    setDownloadingReadersRowId(row.book_id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/admin/royalty-report/${row.book_id}/readers-pdf?${buildWindowQuery({ range, days })}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 60000,
          _noRetry: true,
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (row.title || 'book').replace(/[^a-z0-9]/gi, '_').slice(0, 30);
      link.setAttribute('download', `readers-${safe}-${activeDays}d.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Readers metrics downloaded' });
    } catch (err) {
      const reason = await _extractBlobError(err);
      toast({
        title: 'Readers PDF failed',
        description: reason,
        variant: 'destructive',
      });
    } finally {
      setDownloadingReadersRowId(null);
    }
  };

  const handleDownloadAuthorReadersPdf = async (authorName) => {
    setDownloadingReadersAuthor(authorName);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/admin/royalty-report/by-author/${encodeURIComponent(authorName)}/readers-pdf?${buildWindowQuery({ range, days })}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
          _noRetry: true,
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (authorName || 'author').replace(/[^a-z0-9]/gi, '_').slice(0, 30);
      link.setAttribute('download', `readers-${safe}-${activeDays}d.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `Readers metrics downloaded for ${authorName}` });
    } catch (err) {
      const reason = await _extractBlobError(err);
      toast({
        title: 'Readers PDF failed',
        description: reason,
        variant: 'destructive',
      });
    } finally {
      setDownloadingReadersAuthor(null);
    }
  };

  const toggleAuthor = (authorName) => {
    setCollapsedAuthors((prev) => {
      const next = new Set(prev);
      if (next.has(authorName)) next.delete(authorName);
      else next.add(authorName);
      return next;
    });
  };

  // Chat-history export — opens a small modal, then downloads PDF or CSV
  // bounded by an optional [from, to] date range.
  const openChatExport = (row) => {
    setChatExportRow({ book_id: row.book_id, title: row.title });
    setChatExportFrom('');
    setChatExportTo('');
    setChatExportFormat('pdf');
  };

  const closeChatExport = () => {
    if (chatExporting) return;
    setChatExportRow(null);
  };

  const handleDownloadChatExport = async () => {
    if (!chatExportRow) return;
    setChatExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (chatExportFrom) params.set('from', chatExportFrom);
      if (chatExportTo) params.set('to', chatExportTo);
      params.set('format', chatExportFormat);
      const res = await axios.get(
        `${API}/admin/books/${chatExportRow.book_id}/chat-export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
          _noRetry: true,
        },
      );
      const mime =
        chatExportFormat === 'csv' ? 'text/csv;charset=utf-8' : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (chatExportRow.title || 'book')
        .replace(/[^a-z0-9]/gi, '_')
        .slice(0, 30);
      const rangePart =
        (chatExportFrom ? `_from-${chatExportFrom.replace(/-/g, '')}` : '') +
        (chatExportTo ? `_to-${chatExportTo.replace(/-/g, '')}` : '');
      link.setAttribute('download', `chat-${safe}${rangePart}.${chatExportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Chat history downloaded' });
      setChatExportRow(null);
    } catch (err) {
      const reason = await _extractBlobError(err);
      toast({
        title: 'Chat export failed',
        description: reason,
        variant: 'destructive',
      });
    } finally {
      setChatExporting(false);
    }
  };

  const handlePriceChange = async (row) => {
    const current = row.price !== undefined && row.price !== null ? Number(row.price) : 0;
    const input = window.prompt(
      `Set new price for "${row.title}" (current: $${current.toFixed(2)}):`,
      current.toFixed(2),
    );
    if (input === null) return;
    const next = parseFloat(input);
    if (Number.isNaN(next) || next < 0) {
      toast({ title: 'Invalid price', variant: 'destructive' });
      return;
    }
    setPriceChangeRowId(row.book_id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/royalty-report/${row.book_id}/price`,
        { price: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Price updated', description: `New price: $${next.toFixed(2)}` });
      fetchReport();
    } catch (e) {
      toast({
        title: 'Price update failed',
        description: e.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setPriceChangeRowId(null);
    }
  };

  const fmtMoney = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Build author groups — preserves the existing per-row payout sort order
  // (highest payout author first) so the most valuable authors stay at the
  // top of the list.
  const authorGroups = useMemo(() => {
    const all = report?.rows || [];
    const order = [];          // first-seen order of authors
    const byAuthor = new Map();
    for (const row of all) {
      const name = row.author || 'Unknown';
      if (!byAuthor.has(name)) {
        byAuthor.set(name, []);
        order.push(name);
      }
      byAuthor.get(name).push(row);
    }
    return order.map((name) => {
      const books = byAuthor.get(name) || [];
      // First linked author_user_id wins — surfaces a deep-link to the
      // payout bank details modal even when the book's `author` field is
      // a pen name (e.g., "Mike Trinity" books map to user "Michael T.").
      const author_user_id = books
        .map((b) => b.author_user_id)
        .find((u) => !!u) || null;
      const totals = books.reduce(
        (acc, b) => {
          acc.completions += b.completions || 0;
          acc.partial += b.partial_reads || 0;
          acc.views += b.total_views || 0;
          acc.gross += b.gross_revenue || 0;
          acc.payout += b.author_payout || 0;
          return acc;
        },
        { completions: 0, partial: 0, views: 0, gross: 0, payout: 0 },
      );
      return { name, books, totals, author_user_id };
    });
  }, [report]);

  // Search filter — match author name (keeps all their books) or book title
  // (keeps only matching books, with group totals recomputed).
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return authorGroups;
    return authorGroups
      .map((g) => {
        if ((g.name || '').toLowerCase().includes(q)) return g;
        const books = g.books.filter((b) => (b.title || '').toLowerCase().includes(q));
        if (!books.length) return null;
        const totals = books.reduce(
          (acc, b) => {
            acc.completions += b.completions || 0;
            acc.partial += b.partial_reads || 0;
            acc.views += b.total_views || 0;
            acc.gross += b.gross_revenue || 0;
            acc.payout += b.author_payout || 0;
            return acc;
          },
          { completions: 0, partial: 0, views: 0, gross: 0, payout: 0 },
        );
        return { ...g, books, totals };
      })
      .filter(Boolean);
  }, [authorGroups, search]);

  // FEAT-003 — paginate by AUTHOR group, default to page 1.
  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
  const paginatedGroups = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredGroups.slice(start, start + PAGE_SIZE);
  }, [filteredGroups, page]);

  // Reset to page 1 whenever the underlying data or the search changes
  useEffect(() => {
    setPage(1);
  }, [filteredGroups.length, days, search]);

  return (
    <div className="mt-8 bg-white/5 rounded-xl border border-white/10 p-6" data-testid="royalty-report-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center">
          <DollarSign className="h-6 w-6 text-emerald-500 mr-2" />
          <h2 className="text-xl font-bold text-white">Royalty Report</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search author or book title…"
              className="w-56 bg-zinc-900 border border-white/10 focus:border-emerald-500/60 text-white text-sm rounded-md pl-8 pr-7 py-1.5 placeholder:text-gray-600 focus:outline-none transition-colors"
              data-testid="royalty-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                aria-label="Clear search"
                data-testid="royalty-search-clear"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <label className="text-sm text-gray-400">Window:</label>

          {/* Calendar-style date range picker. Popover holds a 2-month
              range calendar plus quick preset buttons. The trigger button
              always shows the active window so the admin sees it at a glance
              without opening the popover. */}
          <Popover
            open={pickerOpen}
            onOpenChange={(open) => {
              setPickerOpen(open);
              if (open) {
                setDraftRange(range);
                // Re-anchor month/quarter presets to "right now" so they
                // stay accurate if the tab has been idle across midnight.
                setCalendarPresets(computeCalendarPresets());
              }
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 bg-zinc-900 border border-white/10 hover:border-white/30 text-white text-sm rounded-md px-3 py-1.5 transition-colors"
                data-testid="royalty-window-trigger"
              >
                <CalendarRange className="h-4 w-4 text-emerald-400" />
                {range?.from && range?.to ? (
                  <span>
                    {format(range.from, 'MMM d, yyyy')} – {format(range.to, 'MMM d, yyyy')}
                  </span>
                ) : (
                  <span>Last {days} days</span>
                )}
                <ChevronDown className="h-3 w-3 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-auto p-0 bg-zinc-950 border-white/10 text-white"
              data-testid="royalty-window-popover"
            >
              <div className="p-3 flex flex-col gap-3">
                {/* Quick presets row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-gray-400 uppercase tracking-wider mr-1">Quick:</span>
                  {[7, 30, 90, 365].map((n) => (
                    <button
                      key={`preset-${n}`}
                      type="button"
                      onClick={() => applyPreset(n)}
                      className={`text-xs px-2 py-1 rounded-md transition-colors ${
                        !range && days === n
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white/5 hover:bg-white/10 text-gray-200'
                      }`}
                      data-testid={`royalty-preset-${n}`}
                    >
                      {n === 365 ? '1 year' : `${n} days`}
                    </button>
                  ))}
                </div>

                {/* Common accountant windows (this/last month + this/last
                    quarter). These set an explicit date range so the PDF
                    heading reads "May 1 – May 31, 2026" instead of "last
                    31 days". */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-gray-400 uppercase tracking-wider mr-1">Common:</span>
                  {calendarPresets.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyCalendarPreset(preset)}
                      className={`text-xs px-2 py-1 rounded-md transition-colors ${
                        activeCalendarPresetId === preset.id
                          ? 'bg-fuchsia-600 text-white'
                          : 'bg-white/5 hover:bg-white/10 text-gray-200'
                      }`}
                      data-testid={`royalty-cal-preset-${preset.id}`}
                      title={`${format(preset.from, 'MMM d, yyyy')} – ${format(preset.to, 'MMM d, yyyy')}`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                {/* Range calendar */}
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={draftRange}
                  onSelect={setDraftRange}
                  defaultMonth={range?.from || subDays(startOfDay(new Date()), 30)}
                  disabled={(d) => d > new Date()}
                  className="rounded-md border border-white/10 bg-zinc-900"
                />

                {/* Apply / Clear footer */}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="text-xs text-gray-400">
                    {draftRange?.from && draftRange?.to
                      ? `${format(draftRange.from, 'MMM d')} – ${format(draftRange.to, 'MMM d, yyyy')}`
                      : draftRange?.from
                      ? `Pick an end date`
                      : 'Pick a start date'}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {range && (
                      <button
                        type="button"
                        onClick={clearRange}
                        className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white px-2 py-1 rounded-md bg-white/5 hover:bg-white/10"
                        data-testid="royalty-range-clear"
                      >
                        <XIcon className="h-3 w-3" /> Clear
                      </button>
                    )}
                    <Button
                      size="sm"
                      onClick={applyRange}
                      disabled={!draftRange?.from || !draftRange?.to}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs px-3"
                      data-testid="royalty-range-apply"
                    >
                      Apply range
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            onClick={() => fetchReport()}
            disabled={loading}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-royalty-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button
            onClick={handleDownloadPdf}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="download-royalty-pdf-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Download PDF
          </Button>
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-3">
        Calculates per-book author payout = completions × revenue per completion × royalty %. Edit any row's royalty % or # of completions to override the live count, then click Save to persist.
      </p>

      {/* Active reporting window chip — makes it obvious which date range
          the dashboard + every "Download PDF" button on this page is going
          to use. Updates as soon as the dropdown changes. */}
      <div
        className="mb-5 inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 px-3 py-1.5 rounded-full text-sm"
        data-testid="royalty-active-window"
      >
        <CalendarRange className="h-4 w-4" />
        <span className="font-semibold">{(() => {
          const opts = { month: 'short', day: 'numeric', year: 'numeric' };
          if (range?.from && range?.to) {
            return `${range.from.toLocaleDateString(undefined, opts)} – ${range.to.toLocaleDateString(undefined, opts)}`;
          }
          const end = new Date();
          const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
          return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
        })()}</span>
        <span className="text-emerald-300/70">·</span>
        <span>{range?.from && range?.to ? `${activeDays} days` : `Last ${days} days`}</span>
      </div>

      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <div>
          <Label className="text-xs text-gray-400 mb-1 block">Revenue per Completion (USD)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={revenuePerCompletion}
            onChange={(e) => setRevenuePerCompletion(e.target.value)}
            className="bg-white/5 border-white/10 text-white w-40"
            data-testid="revenue-per-completion"
          />
        </div>
        <Button
          onClick={handleSaveSettings}
          disabled={savingSettings}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-revenue-btn"
        >
          {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
        </Button>
      </div>

      {report?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryCard label="Total Completions" value={report.totals.completions.toLocaleString()} />
          <SummaryCard label="Partial Reads" value={report.totals.partial_reads.toLocaleString()} />
          <SummaryCard label="Gross Revenue" value={fmtMoney(report.totals.gross_revenue)} accent="emerald" />
          <SummaryCard label="Author Payout" value={fmtMoney(report.totals.author_payout)} accent="amber" />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="royalty-table">
          <thead>
            <tr className="text-left text-gray-400 border-b border-white/10">
              <th className="py-2 px-3">Title</th>
              <th className="py-2 px-3">Author</th>
              <th className="py-2 px-3 text-right">Completions</th>
              <th className="py-2 px-3 text-right">Partial</th>
              <th className="py-2 px-3 text-right" title={`Views in the selected ${activeDays}-day window`}>Views ({activeDays}d)</th>
              <th className="py-2 px-3 text-right">Engmt</th>
              <th className="py-2 px-3 text-right">Royalty %</th>
              <th className="py-2 px-3 text-right">Payout</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedGroups.map((group) => {
              const isCollapsed = collapsedAuthors.has(group.name);
              const authorPdfLoading = downloadingAuthor === group.name;
              return (
                <React.Fragment key={`group-${group.name}`}>
                  {/* Author header row — totals + collapse toggle + per-author PDF */}
                  <tr
                    className="bg-fuchsia-500/10 border-b border-fuchsia-400/20 hover:bg-fuchsia-500/15 transition-colors"
                    data-testid={`author-group-${group.name}`}
                  >
                    <td className="py-2.5 px-3" colSpan={2}>
                      <button
                        type="button"
                        onClick={() => toggleAuthor(group.name)}
                        className="inline-flex items-center gap-2 text-left text-white font-bold hover:text-fuchsia-200 transition-colors"
                        data-testid={`author-toggle-${group.name}`}
                        aria-expanded={!isCollapsed}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4 text-fuchsia-300" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-fuchsia-300" />
                        )}
                        <span>{group.name}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] text-fuchsia-200/80 font-semibold bg-fuchsia-500/20 px-1.5 py-0.5 rounded-full">
                          <BookOpen className="h-3 w-3" />
                          {group.books.length} book{group.books.length === 1 ? '' : 's'}
                        </span>
                      </button>
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs text-gray-300">
                      {group.totals.completions.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs text-gray-300">
                      {group.totals.partial.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs text-gray-300">
                      {group.totals.views.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3"></td>
                    <td className="py-2.5 px-3"></td>
                    <td
                      className="py-2.5 px-3 text-right font-bold text-emerald-300"
                      data-testid={`author-payout-${group.name}`}
                    >
                      {fmtMoney(group.totals.payout)}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setBankModalUserId(group.author_user_id)}
                          disabled={!group.author_user_id}
                          className="h-7 text-xs px-2 text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                          title={
                            group.author_user_id
                              ? `View ${group.name}'s payout bank details`
                              : `Link an author user to one of ${group.name}'s books to view bank details`
                          }
                          data-testid={`author-bank-${group.name}`}
                        >
                          <Banknote className="h-3 w-3 mr-1" />
                          Bank
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDownloadAuthorPdf(group.name)}
                          disabled={authorPdfLoading}
                          className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-7 text-xs px-2"
                          title={`Download ALL of ${group.name}'s books in one PDF`}
                          data-testid={`author-pdf-${group.name}`}
                        >
                          {authorPdfLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <FileDown className="h-3 w-3 mr-1" />
                              Author PDF
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDownloadAuthorReadersPdf(group.name)}
                          disabled={downloadingReadersAuthor === group.name}
                          className="bg-teal-600 hover:bg-teal-700 text-white h-7 text-xs px-2"
                          title={`Download Readers Completion Metrics for ALL of ${group.name}'s books`}
                          data-testid={`author-readers-pdf-${group.name}`}
                        >
                          {downloadingReadersAuthor === group.name ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <Users className="h-3 w-3 mr-1" />
                              Readers
                            </>
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>

                  {/* Per-book rows for this author (indented) */}
                  {!isCollapsed && group.books.map((row) => {
                    const e = edits[row.book_id] || {};
                    const royaltyVal = e.royalty_percent !== undefined ? e.royalty_percent : row.royalty_percent;
                    const completionsVal =
                      e.completions_override !== undefined
                        ? e.completions_override
                        : row.completions_override !== null && row.completions_override !== undefined
                        ? String(row.completions_override)
                        : '';
                    const partialVal =
                      e.partial_override !== undefined
                        ? e.partial_override
                        : row.partial_override !== null && row.partial_override !== undefined
                        ? String(row.partial_override)
                        : '';
                    const isDirty =
                      e.royalty_percent !== undefined
                      || e.completions_override !== undefined
                      || e.partial_override !== undefined;
                    return (
                      <tr
                        key={row.book_id}
                        className={`border-b border-white/5 hover:bg-white/5 ${isDirty ? 'bg-amber-500/5' : ''}`}
                        data-testid={`royalty-row-${row.book_id}`}
                      >
                        <td className="py-2 pl-9 pr-3 text-white max-w-[260px] truncate" title={row.title}>
                          {/* Indent + small bullet so it visually nests under the author */}
                          <span className="text-fuchsia-400/40 mr-2">└</span>
                          {row.title}
                          {/* Tiny price chip — instant visual confirmation that
                              a price change has been persisted. Amber when
                              the book has a per-book override, neutral grey
                              when it falls back to the platform default. */}
                          <span
                            className={`ml-2 inline-flex items-center text-[10px] font-semibold rounded-full px-1.5 py-0.5 align-middle border ${
                              row.price_is_overridden
                                ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                                : 'text-gray-400 bg-white/5 border-white/15'
                            }`}
                            data-testid={`row-price-chip-${row.book_id}`}
                            title={
                              row.price_is_overridden
                                ? `Per-book price override: $${Number(row.price || 0).toFixed(2)}`
                                : `Using default ($${Number(row.revenue_per_completion || 0).toFixed(2)}) — no per-book override`
                            }
                          >
                            ${Number(row.price_is_overridden ? row.price : row.revenue_per_completion || 0).toFixed(2)}
                            {row.price_is_overridden && <span className="ml-1">★</span>}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-500 text-xs italic">{row.author}</td>
                        <td className="py-2 px-3 text-right">
                          <input
                            type="number"
                            min="0"
                            placeholder={String(row.live_completions)}
                            value={completionsVal}
                            onChange={(ev) => handleEditChange(row.book_id, 'completions_override', ev.target.value)}
                            className="w-20 bg-white/5 border border-white/10 text-white rounded px-2 py-1 text-right text-sm"
                            data-testid={`completions-${row.book_id}`}
                          />
                          <div className="text-[10px] text-gray-500">live: {row.live_completions}</div>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <input
                            type="number"
                            min="0"
                            placeholder={String(row.live_partial ?? 0)}
                            value={partialVal}
                            onChange={(ev) => handleEditChange(row.book_id, 'partial_override', ev.target.value)}
                            className="w-20 bg-white/5 border border-white/10 text-white rounded px-2 py-1 text-right text-sm"
                            data-testid={`partial-${row.book_id}`}
                          />
                          <div className="text-[10px] text-gray-500">live: {row.live_partial ?? 0}</div>
                        </td>
                        <td className="py-2 px-3 text-right text-gray-300">{row.total_views.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-gray-300">{row.avg_engagement}</td>
                        <td className="py-2 px-3 text-right">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={royaltyVal}
                            onChange={(ev) => handleEditChange(row.book_id, 'royalty_percent', ev.target.value)}
                            className="w-20 bg-white/5 border border-white/10 text-white rounded px-2 py-1 text-right text-sm"
                            data-testid={`royalty-${row.book_id}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-right font-bold text-emerald-400" data-testid={`payout-${row.book_id}`}>
                          {fmtMoney(row.author_payout)}
                        </td>
                        <td className="py-2 px-3 min-w-[200px]">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            <Button
                              size="sm"
                              onClick={() => handleSaveRow(row)}
                              disabled={!isDirty || savingRowId === row.book_id}
                              className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs px-2"
                              data-testid={`save-royalty-${row.book_id}`}
                            >
                              {savingRowId === row.book_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handlePriceChange(row)}
                              disabled={priceChangeRowId === row.book_id}
                              className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs px-2"
                              title={`Change price (current $${Number(row.price || 0).toFixed(2)})`}
                              data-testid={`price-change-${row.book_id}`}
                            >
                              {priceChangeRowId === row.book_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <Tag className="h-3 w-3 mr-1" />
                                  Price
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleDownloadRowPdf(row)}
                              disabled={downloadingRowId === row.book_id}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-xs px-2"
                              title="Download this book's royalty PDF"
                              data-testid={`row-pdf-${row.book_id}`}
                            >
                              {downloadingRowId === row.book_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <FileDown className="h-3 w-3 mr-1" />
                                  PDF
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleDownloadReadersPdf(row)}
                              disabled={downloadingReadersRowId === row.book_id}
                              className="bg-teal-600 hover:bg-teal-700 text-white h-7 text-xs px-2"
                              title="Download Readers Completion Metrics for this book"
                              data-testid={`row-readers-pdf-${row.book_id}`}
                            >
                              {downloadingReadersRowId === row.book_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <Users className="h-3 w-3 mr-1" />
                                  Readers
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => openChatExport(row)}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-xs px-2"
                              title="Export Book Discussion chat history (PDF or CSV)"
                              data-testid={`row-chat-export-${row.book_id}`}
                            >
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Chat
                            </Button>
                            {row.is_overridden && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResetRow(row)}
                                className="text-gray-500 hover:text-white h-7 px-1"
                                title="Reset overrides"
                                data-testid={`reset-royalty-${row.book_id}`}
                              >
                                <RotateCcw className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {(!report?.rows || report.rows.length === 0) && !loading && (
          <p className="text-center py-8 text-gray-500" data-testid="royalty-empty">No books to report on.</p>
        )}
        {report?.rows?.length > 0 && filteredGroups.length === 0 && search.trim() && !loading && (
          <p className="text-center py-8 text-gray-500" data-testid="royalty-search-empty">
            No authors or books match "{search.trim()}".
          </p>
        )}
      </div>

      {/* Pagination — paginates by AUTHOR group now (PAGE_SIZE = 8 authors) */}
      {authorGroups.length > PAGE_SIZE && (
        <div
          className="flex items-center justify-between mt-3 pt-3 border-t border-white/10"
          data-testid="royalty-pagination"
        >
          <p className="text-xs text-gray-500">
            Showing authors{' '}
            <span className="text-white font-medium">
              {Math.min(filteredGroups.length, (page - 1) * PAGE_SIZE + 1)}–{Math.min(filteredGroups.length, page * PAGE_SIZE)}
            </span>{' '}
            of <span className="text-white font-medium">{filteredGroups.length}</span>
            {search.trim() && (
              <span className="text-gray-500"> (filtered)</span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="royalty-prev-page"
              className="bg-white/5 hover:bg-white/10 h-7 px-2 text-xs"
            >
              <ChevronLeft className="h-3 w-3 mr-1" /> Prev
            </Button>
            <span className="text-xs text-gray-300" data-testid="royalty-page-indicator">
              Page <span className="text-white font-medium">{page}</span> / {totalPages}
            </span>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="royalty-next-page"
              className="bg-white/5 hover:bg-white/10 h-7 px-2 text-xs"
            >
              Next <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Per-author payout bank details modal (admin view + edit) */}
      {bankModalUserId && (
        <AdminBankDetailsModal
          user_id={bankModalUserId}
          onClose={() => setBankModalUserId(null)}
        />
      )}

      {/* Chat-history export modal */}
      {chatExportRow && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeChatExport}
          data-testid="chat-export-modal"
        >
          <div
            className="bg-gray-900 border border-white/10 rounded-xl w-full max-w-md p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Export chat history
                </h3>
                <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                  {chatExportRow.title}
                </p>
              </div>
              <button
                type="button"
                onClick={closeChatExport}
                disabled={chatExporting}
                className="text-gray-400 hover:text-white disabled:opacity-50"
                data-testid="chat-export-close"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-300">
                    From <span className="text-gray-500">(optional)</span>
                  </Label>
                  <Input
                    type="date"
                    value={chatExportFrom}
                    onChange={(e) => setChatExportFrom(e.target.value)}
                    className="bg-white/5 border-white/10 text-white text-sm"
                    data-testid="chat-export-from"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-300">
                    To <span className="text-gray-500">(optional)</span>
                  </Label>
                  <Input
                    type="date"
                    value={chatExportTo}
                    onChange={(e) => setChatExportTo(e.target.value)}
                    className="bg-white/5 border-white/10 text-white text-sm"
                    data-testid="chat-export-to"
                  />
                </div>
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Leave both blank to export the entire chat history. Dates are
                inclusive and interpreted in UTC.
              </p>

              <div>
                <Label className="text-xs text-gray-300">Format</Label>
                <div className="flex gap-2 mt-1.5">
                  {['pdf', 'csv'].map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setChatExportFormat(f)}
                      className={`flex-1 py-2 rounded border text-xs uppercase tracking-wide transition ${
                        chatExportFormat === f
                          ? 'bg-indigo-600 border-indigo-500 text-white'
                          : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                      }`}
                      data-testid={`chat-export-format-${f}`}
                    >
                      {f === 'pdf' ? 'PDF (readable)' : 'CSV (spreadsheet)'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeChatExport}
                  disabled={chatExporting}
                  className="text-gray-300"
                  data-testid="chat-export-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleDownloadChatExport}
                  disabled={chatExporting}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="chat-export-confirm"
                >
                  {chatExporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Exporting…
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, accent }) => {
  const accentCls = accent === 'emerald'
    ? 'text-emerald-400'
    : accent === 'amber'
    ? 'text-amber-400'
    : 'text-white';
  return (
    <div className="bg-white/5 rounded-lg p-3">
      <p className={`text-xl font-bold ${accentCls}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

export default RoyaltyReportPanel;
